import { useCallback, useEffect } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { getWindowSafe } from '@change-corgi/core/window';

import {
	useStarterDashboardV2PageData,
	useStarterDashboardV2PageNotificationManagerContext,
} from 'src/app/pages/starterDashboardV2/pageContext';
import { useCopyLinkButton, useSharePetitionButton } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useShareUrl } from '../../../../../../../PageBody/components/GrowPetition/components/ShareYourPetition/components/ShareUrl/hooks/useShareUrl';
import { Toast } from '../../../../../../../PageBody/components/Toast';

export type UseActionButtonsResult = ModelHookResult<
	undefined,
	{
		handleEditPetitionClick: () => void;
		handleViewPetitionClick: () => void;
		handleCopyLinkClick: () => void;
		handleSharePetitionClick: () => Promise<void>;
	}
>;

export const EDIT_CLICK_EVENT_NAME = 'petition_manage_edit_click';
export const VIEW_CLICK_EVENT_NAME = 'petition_manage_view_click';

export function useActionButtons(): UseActionButtonsResult {
	const {
		data: {
			petition: {
				signatureState: {
					signatureCount: { displayed: signatureCount },
				},
				slug,
			},
		},
	} = useStarterDashboardV2PageData();
	const {
		data: { shareUrl },
	} = useShareUrl();
	const {
		data: { copied },
		actions: { handleShare },
	} = useCopyLinkButton({ shareUrl });
	const {
		actions: { handleShareClick },
	} = useSharePetitionButton();
	const track = useTracking();
	const window = getWindowSafe();
	const {
		actions: { addNotification },
	} = useStarterDashboardV2PageNotificationManagerContext();
	const { translate } = useI18n();

	useEffect(() => {
		if (copied) {
			addNotification({
				id: 'header-copy-link',
				content: (
					<Toast
						message={translate('corgi.petition.starter_dashboard.header_navigation.copy_link_button.copied_state_2')}
					/>
				),
			});
		}
	}, [copied, addNotification, translate]);

	const trackAndNewWindow = useCallback(
		(eventName: string, url: string) => () => {
			// Track the event
			void track(eventName, { num_signatures: signatureCount });
			// Open the URL in a new window/tab
			if (window) {
				window.open(url, '_blank');
			}
		},
		[window, signatureCount, track],
	);

	return {
		actions: {
			handleEditPetitionClick: trackAndNewWindow(EDIT_CLICK_EVENT_NAME, `/p/${encodeURIComponent(slug)}/edit`),
			handleViewPetitionClick: trackAndNewWindow(VIEW_CLICK_EVENT_NAME, `/p/${encodeURIComponent(slug)}`),
			handleCopyLinkClick: handleShare,
			handleSharePetitionClick: async () =>
				handleShareClick({
					source_location: 'petition_dashboard_share_modal',
					view_context: 'petition_dashboard_share_modal',
				}),
		},
	};
}
